import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

import TableService from '../../services/TableService';
import {
  ensurePaginationInUrl,
  pushPaginationToUrl,
  readPaginationFromSearch,
  resetPageOnFilterChange,
} from './tablePaginationUtils';
import {
  tableV2ContainerSx,
  tableV2HeaderActionsBoxSx,
  tableV2HeaderButtonSx,
  tableV2AreaSx,
  TABLE_V2_ROW_HEIGHT_COMPACT,
  tableV2ActionCellSx,
  TABLE_V2_ACTION_BUTTON_WIDTH,
} from './tableV2Styles';

const TableV2 = ({
  columns,
  rows: externalRows,
  useApi = false,
  fetchUrl,
  paginationUrl,
  filterData,
  headerActions,
  actions,
  onEdit,
  onDelete,
  canDelete,
  onView,
  pageSize = 10,
  autoPageSize = true,
  density,
  rowHeight = 44,
  getRowHeight,
  gridSx,
  emptyMessage = 'Nenhum registro encontrado',
  refreshKey,
  sortComparator,
  rowMapper,
  actionsColumnWidth,
  /** Loading controlado (ex.: fetch local sem useApi). Se omitido, usa o estado interno do useApi. */
  loading: loadingProp,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loadingInternal, setLoading] = useState(false);
  const loading = loadingProp !== undefined ? loadingProp : loadingInternal;

  const [page, setPage] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  const [deleteId, setDeleteId] = useState(null);
  const filterDataMountRef = useRef(true);

  const paginationBase = paginationUrl || location.pathname;
  const effectiveAutoPageSize = useApi ? false : autoPageSize;
  const useManualClientPagination = !useApi && !effectiveAutoPageSize;
  const effectiveRowHeight = density === 'compact'
    ? TABLE_V2_ROW_HEIGHT_COMPACT
    : rowHeight;

  useLayoutEffect(() => {
    if (!useApi) return;
    const { page: p, pageSize: s } = readPaginationFromSearch(location.search, pageSize);
    setPage(p);
    setPageSizeState(s);
  }, [useApi, location.search, pageSize]);

  useEffect(() => {
    if (!useApi) return;
    ensurePaginationInUrl(history, paginationBase, location.search, pageSize);
  }, [useApi, paginationBase, location.search, pageSize, history]);

  useEffect(() => {
    if (!useApi) {
      setPage(0);
      filterDataMountRef.current = false;
      return;
    }
    if (filterDataMountRef.current) {
      filterDataMountRef.current = false;
      return;
    }
    resetPageOnFilterChange(
      history,
      paginationBase,
      location.search,
      pageSizeState,
      pageSize
    );
    setPage(0);
  }, [filterData]);

  // =========================
  // 📡 FETCH DATA
  // =========================
  const fetchData = async () => {
    if (!useApi) return;

    setLoading(true);

    try {
      const response = await TableService.request(
        `${fetchUrl}?p=${page}&s=${pageSizeState}`,
        filterData
      );

      // Endpoints paginados: { rows, totalRows }. Legado (ex.: usuarios/filtro): array puro.
      const data = response?.data;
      let nextRows;
      let nextCount;
      if (Array.isArray(data)) {
        let all = data;
        if (rowMapper) all = all.map(rowMapper);
        if (sortComparator) all = [...all].sort(sortComparator);
        nextCount = all.length;
        nextRows = all.slice(
          page * pageSizeState,
          page * pageSizeState + pageSizeState
        );
      } else {
        nextRows = data?.rows || [];
        nextCount = data?.totalRows || 0;
        if (rowMapper && Array.isArray(nextRows)) {
          nextRows = nextRows.map(rowMapper);
        }
        if (sortComparator && Array.isArray(nextRows)) {
          nextRows = [...nextRows].sort(sortComparator);
        }
      }
      setRows(nextRows);
      setRowCount(nextCount);
    } catch (e) {
      console.error(e);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (useApi) fetchData();
  }, [page, pageSizeState, filterData, refreshKey]);

  useEffect(() => {
    if (!useApi && externalRows) {
      const nextRows = sortComparator ? [...externalRows].sort(sortComparator) : externalRows;
      setRows(nextRows);
      setRowCount(nextRows.length);
    }
  }, [externalRows, useApi, sortComparator]);

  const visibleRows = useManualClientPagination
    ? rows.slice(page * pageSizeState, page * pageSizeState + pageSizeState)
    : rows;

  const builtinActionCount =
    (onEdit ? 1 : 0) + (onDelete ? 1 : 0) + (onView ? 1 : 0);
  const customActionCount = actions?.length || 0;
  const computedActionsWidth = Math.max(
    120,
    (customActionCount + builtinActionCount) * TABLE_V2_ACTION_BUTTON_WIDTH + 12
  );
  const resolvedActionsWidth = actionsColumnWidth ?? computedActionsWidth;

  // =========================
  // ⚙️ ACTION COLUMN
  // =========================
  const actionColumn = {
    field: 'actions',
    headerName: 'Ações',
    width: resolvedActionsWidth,
    minWidth: resolvedActionsWidth,
    maxWidth: resolvedActionsWidth,
    sortable: false,
    disableColumnMenu: true,
    resizable: false,
    renderCell: (params) => (
      <Box sx={tableV2ActionCellSx}>
        {actions && actions.map((action) => {
          const visible = !action.show || action.show(params.row);
          if (!visible) return null;
          const btn = (
            <IconButton
              key={action.key}
              onClick={() => action.onClick(params.row.id, params.row)}
              size="small"
            >
              {action.icon}
            </IconButton>
          );
          return action.title ? (
            <Tooltip
              key={action.key}
              title={action.title}
            >
              <span>{btn}</span>
            </Tooltip>
          ) : btn;
        })}

        {onEdit && (
          <IconButton size="small" onClick={() => onEdit(params.row.id, params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}

        {onView && (
          <IconButton size="small" onClick={() => onView(params.row.id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )}

        {onDelete && (!canDelete || canDelete(params.row)) && (
          <IconButton size="small" onClick={() => setDeleteId(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )
  };

  const hasActions = onEdit || onDelete || onView || (actions && actions.length > 0);
  const finalColumns = hasActions ? [...columns, actionColumn] : columns;

  // =========================
  // 🧱 RENDER
  // =========================
  return (
    <Box sx={tableV2ContainerSx}>
      {headerActions && headerActions.length > 0 && (
        <Box sx={tableV2HeaderActionsBoxSx}>
          {headerActions.map((action, i) => {
            if (action.node) {
              return (
                <Box key={action.key || i} component="span" sx={tableV2HeaderButtonSx(i)}>
                  {action.node}
                </Box>
              );
            }

            return (
              <Button
                key={action.key || i}
                variant={action.variant || 'contained'}
                startIcon={action.startIcon}
                onClick={action.onClick}
                size={action.size || 'medium'}
                sx={tableV2HeaderButtonSx(i)}
              >
                {action.label}
              </Button>
            );
          })}
        </Box>
      )}

      {/* TABLE */}
      <Box sx={tableV2AreaSx}>
        <DataGrid
          rows={visibleRows}
          columns={finalColumns}
          pagination
          autoPageSize={effectiveAutoPageSize}
          density={density === 'compact' ? 'compact' : undefined}
          rowHeight={effectiveRowHeight}
          getRowHeight={getRowHeight}
          paginationMode={useApi || useManualClientPagination ? 'server' : 'client'}
          rowCount={rowCount}
          paginationModel={{ page, pageSize: pageSizeState }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSizeState(model.pageSize);
            if (useApi) {
              pushPaginationToUrl(
                history,
                paginationBase,
                location.search,
                model.page,
                model.pageSize
              );
            }
          }}
          pageSizeOptions={effectiveAutoPageSize ? [] : [10, 20, 50]}
          localeText={{
            ...ptBR.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: emptyMessage,
            noResultsOverlayLabel: emptyMessage,
          }}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
          getRowClassName={(params) => (
            params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
          )}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => (
                theme.palette.mode === 'dark'
                  ? theme.palette.neutral[900]
                  : theme.palette.neutral[50]
              ),
              fontWeight: 600
            },
            '& .MuiDataGrid-row.row-even': {
              backgroundColor: (theme) => (
                theme.palette.mode === 'dark'
                  ? theme.palette.neutral[900]
                  : theme.palette.neutral[50]
              )
            },
            '& .MuiDataGrid-row.row-odd': {
              backgroundColor: (theme) => (
                theme.palette.mode === 'dark'
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[100]
              )
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: (theme) => theme.palette.action.hover
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none'
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none'
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              overflow: 'visible',
              px: 0.5,
            },
            ...gridSx,
          }}
        />
      </Box>

      {/* DELETE DIALOG */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>
            Cancelar
          </Button>
          <Button
            color="error"
            onClick={() => {
              onDelete(deleteId);
              setDeleteId(null);
            }}
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

TableV2.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  useApi: PropTypes.bool,
  fetchUrl: PropTypes.string,
  /** Path usado na URL de paginação (ex.: legado `convocacoes`). Se omitido, usa `location.pathname`. */
  paginationUrl: PropTypes.string,
  filterData: PropTypes.object,
  headerActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      onClick: PropTypes.func,
      startIcon: PropTypes.node,
      variant: PropTypes.string,
      size: PropTypes.string,
      node: PropTypes.node,
    })
  ),
  /** Ações genéricas por linha: [{ key, icon, title?, show?: (row) => bool, onClick: (id, row) => void }] */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
      title: PropTypes.string,
      show: PropTypes.func,
      onClick: PropTypes.func.isRequired,
    })
  ),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  /** Quando definido, controla por linha se o botão deletar aparece: `(row) => boolean` */
  canDelete: PropTypes.func,
  onView: PropTypes.func,
  pageSize: PropTypes.number,
  autoPageSize: PropTypes.bool,
  /** `'compact'` usa rowHeight 36 e density compact do DataGrid */
  density: PropTypes.oneOf(['compact']),
  rowHeight: PropTypes.number,
  /** Altura dinâmica por linha; use `() => 'auto'` com `gridSx` multiline para células empilhadas */
  getRowHeight: PropTypes.func,
  /** Estilos extras mesclados no `sx` do DataGrid */
  gridSx: PropTypes.object,
  emptyMessage: PropTypes.string,
  refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /**
   * Ordenação estável dentro do conjunto de linhas exibidas.
   * Com `useApi`: aplica só na página atual retornada pelo backend — útil quando a API não garante ordem após filtros/atualizações.
   * Ordem global entre todas as páginas exige sort no servidor.
   */
  sortComparator: PropTypes.func,
  rowMapper: PropTypes.func,
  /** Largura fixa da coluna de ações; se omitido, calculada pelo número de botões */
  actionsColumnWidth: PropTypes.number,
  /** Quando definido, controla o overlay de loading do DataGrid (útil sem useApi). */
  loading: PropTypes.bool,
};

TableV2.defaultProps = {
  rows: undefined,
  useApi: false,
  fetchUrl: '',
  paginationUrl: '',
  filterData: undefined,
  headerActions: undefined,
  actions: undefined,
  onEdit: undefined,
  onDelete: undefined,
  canDelete: undefined,
  onView: undefined,
  pageSize: 10,
  autoPageSize: true,
  density: undefined,
  rowHeight: 44,
  getRowHeight: undefined,
  gridSx: undefined,
  emptyMessage: 'Nenhum registro encontrado',
  refreshKey: undefined,
  sortComparator: null,
  actionsColumnWidth: undefined,
  loading: undefined,
};

export default TableV2;
